/** XeAgents.ai**/
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, UserCheck, UserX, CreditCard, Download, Loader2, Gift, Trash2, AlertTriangle, Mail, Copy, Building2, Wrench, RefreshCw, Plus, Send, Check, Clock, XCircle, ExternalLink } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import Papa from "papaparse";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { format } from "date-fns";
import { formatDate } from "@/lib/date-format";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "react-i18next";
import { AuthStorage } from "@/lib/auth-storage";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  credits: number;
  planType: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
  plan?: {
    displayName: string;
  };
  isEnterprise?: boolean;
  enterpriseOnboarding?: {
    id: string;
    organizationName: string;
    planAmount: string;
    recurringMonthlyAmount: string | null;
    perMinuteRate: string;
    includedMinutes: number;
    currency: string;
    paymentStatus: string;
    paymentLink: string;
  } | null;
  setupPhases?: {
    total: number;
    paid: number;
    pending: number;
    required: number;
    requiredPaid: number;
  } | null;
}

interface SetupPhase {
  id: string;
  phaseNumber: number;
  phaseName: string;
  description: string;
  amount: string;
  isRequired: boolean;
  paymentStatus: string;
  paidAt: string | null;
  dueDate: string | null;
  stripePaymentLinkUrl: string | null;
}

interface PaginatedResponse {
  data: User[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export default function UserManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isSuperAdmin = AuthStorage.isSuperAdmin();
  const [editDialog, setEditDialog] = useState(false);
  const [promoCreditsDialog, setPromoCreditsDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    credits: 0,
    role: "user",
    planType: "free"
  });
  const [promoCreditsAmount, setPromoCreditsAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isExporting, setIsExporting] = useState(false);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string; password: string; emailSent: boolean;
  } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>("US");
  const [phasesDialog, setPhasesDialog] = useState(false);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [userPhases, setUserPhases] = useState<SetupPhase[]>([]);
  const [addPhaseDialog, setAddPhaseDialog] = useState(false);
  const [newPhase, setNewPhase] = useState({ phaseName: "", description: "", amount: 0, isRequired: true });

  const { data: response, isLoading, refetch } = useQuery<PaginatedResponse>({
    queryKey: [`/api/admin/users?page=${page}&pageSize=${pageSize}&country=${selectedCountry}`],
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const users = response?.data || [];
  const pagination = response?.pagination || { page: 1, pageSize: 25, totalItems: 0, totalPages: 1 };

  const updateUser = useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: any }) => {
      return apiRequest("PATCH", `/api/admin/users/${userId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: t("admin.users.userUpdated") });
      setEditDialog(false);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: t("admin.users.updateFailed"),
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const addPromoCredits = useMutation({
    mutationFn: async ({ userId, amount }: { userId: string; amount: number }) => {
      return apiRequest("POST", `/api/admin/users/${userId}/credits`, { 
        amount, 
        description: "Promo Credits" 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: t("admin.users.promoCreditsAdded") });
      setPromoCreditsDialog(false);
      setPromoCreditsAmount(0);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: t("admin.users.promoFailed"),
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const resendCredentials = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("POST", `/api/admin/users/${userId}/resend-credentials`, {});
      return response.json();
    },
    onSuccess: (data: any) => {
      setCreatedCredentials({
        email: selectedUser!.email,
        password: data.temporaryPassword,
        emailSent: data.emailSent,
      });
      setCredentialsDialog(true);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to resend credentials",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User deleted successfully", description: "All user data has been permanently removed." });
      setDeleteConfirmDialog(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Phase management mutations
  const sendPhasePaymentLink = useMutation({
    mutationFn: async ({ userId, phaseId }: { userId: string; phaseId: string }) => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/setup-phases/${phaseId}/send-payment-link`, {});
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Payment link sent", description: data.emailSent ? "Email sent successfully" : "Link generated (email not sent)" });
      if (selectedUser) fetchUserPhases(selectedUser.id);
    },
    onError: (error: any) => {
      toast({ title: "Failed to send payment link", description: error.message, variant: "destructive" });
    }
  });

  const sendRecurringPaymentLink = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/send-recurring-payment-link`, {});
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Subscription link sent", description: data.emailSent ? "Email sent successfully" : "Link generated (email not sent)" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to send subscription link", description: error.message, variant: "destructive" });
    }
  });

  const addPhaseMutation = useMutation({
    mutationFn: async ({ userId, phase }: { userId: string; phase: typeof newPhase }) => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/setup-phases`, phase);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Phase added successfully" });
      setAddPhaseDialog(false);
      setNewPhase({ phaseName: "", description: "", amount: 0, isRequired: true });
      if (selectedUser) fetchUserPhases(selectedUser.id);
      refetch();
    },
    onError: (error: any) => {
      toast({ title: "Failed to add phase", description: error.message, variant: "destructive" });
    }
  });

  const updatePhaseMutation = useMutation({
    mutationFn: async ({ userId, phaseId, updates }: { userId: string; phaseId: string; updates: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${userId}/setup-phases/${phaseId}`, updates);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Phase updated successfully" });
      if (selectedUser) fetchUserPhases(selectedUser.id);
      refetch();
    },
    onError: (error: any) => {
      toast({ title: "Failed to update phase", description: error.message, variant: "destructive" });
    }
  });

  const deletePhaseMutation = useMutation({
    mutationFn: async ({ userId, phaseId }: { userId: string; phaseId: string }) => {
      return apiRequest("DELETE", `/api/admin/users/${userId}/setup-phases/${phaseId}`);
    },
    onSuccess: () => {
      toast({ title: "Phase deleted successfully" });
      if (selectedUser) fetchUserPhases(selectedUser.id);
      refetch();
    },
    onError: (error: any) => {
      toast({ title: "Failed to delete phase", description: error.message, variant: "destructive" });
    }
  });

  const fetchUserPhases = async (userId: string) => {
    setPhasesLoading(true);
    try {
      const res = await apiRequest("GET", `/api/admin/users/${userId}/setup-phases`);
      const data = await res.json();
      setUserPhases(data.phases || []);
    } catch (error) {
      console.error("Error fetching phases:", error);
      setUserPhases([]);
    } finally {
      setPhasesLoading(false);
    }
  };

  const handleOpenPhasesDialog = async (user: User) => {
    setSelectedUser(user);
    setPhasesDialog(true);
    await fetchUserPhases(user.id);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      credits: user.credits,
      role: user.role,
      planType: user.planType
    });
    setEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!selectedUser) return;
    updateUser.mutate({
      userId: selectedUser.id,
      updates: editForm
    });
  };

  const handleOpenPromoCredits = (user: User) => {
    setSelectedUser(user);
    setPromoCreditsAmount(0);
    setPromoCreditsDialog(true);
  };

  const handleAddPromoCredits = () => {
    if (!selectedUser || promoCreditsAmount <= 0) return;
    addPromoCredits.mutate({
      userId: selectedUser.id,
      amount: promoCreditsAmount
    });
  };

  const toggleActive = (user: User) => {
    updateUser.mutate({
      userId: user.id,
      updates: { isActive: !user.isActive }
    });
  };

  const handleOpenDeleteConfirm = (user: User) => {
    setSelectedUser(user);
    setDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedUser) return;
    deleteUser.mutate(selectedUser.id);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await apiRequest("GET", `/api/admin/users?page=1&pageSize=999999&country=${selectedCountry}`);
      if (!response.ok) {
        throw new Error("Failed to fetch users for export");
      }
      
      const result = await response.json();
      const allUsers = result.data;

      const csvData = allUsers.map((user: User) => ({
        "Name": user.name,
        "Email": user.email,
        "Role": user.role,
        "Plan": user.plan?.displayName || user.planType,
        "Credits": user.credits,
        "Status": user.isActive ? "Active" : "Inactive",
        "Super Admin": user.isSuperAdmin ? "Yes" : "No",
        "Joined": user.createdAt ? formatDate(user.createdAt, "yyyy-MM-dd HH:mm:ss") : ""
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `users-export-${format(new Date(), "yyyy-MM-dd-HHmmss")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: t("admin.users.exportSuccess"),
        description: t("admin.users.exportedUsers", { count: allUsers.length }),
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: t("admin.users.exportFailed"),
        description: t("admin.users.exportFailedDesc"),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="w-40">
            <Select
              value={selectedCountry}
              onValueChange={(value) => {
                setSelectedCountry(value);
                setPage(1); // Reset to first page when changing country
              }}
            >
              <SelectTrigger data-testid="select-country-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="IN">India</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold">{t("admin.users.title")}</h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {t("admin.users.description")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isSuperAdmin && (
            <Button 
              onClick={handleExportCSV} 
              variant="outline" 
              size="sm"
              disabled={isExporting || users.length === 0}
              data-testid="button-export-users"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("admin.users.exporting")}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {t("admin.users.exportCSV")}
                </>
              )}
            </Button>
          )}
          <Button onClick={() => refetch()} variant="outline" size="sm" data-testid="button-refresh-users">
            {t("common.refresh")}
          </Button>
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableCaption>{t("admin.users.tableCaption")}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.users.columns.user")}</TableHead>
              <TableHead className="hidden sm:table-cell">{t("admin.users.columns.role")}</TableHead>
              <TableHead>{t("admin.users.columns.plan")}</TableHead>
              <TableHead>{t("admin.users.columns.credits")}</TableHead>
              <TableHead>{t("admin.users.columns.status")}</TableHead>
              <TableHead className="hidden md:table-cell">{t("admin.users.columns.joined")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  {t("admin.users.loadingUsers")}
                </TableCell>
              </TableRow>
            ) : users?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  {t("admin.users.noUsersFound")}
                </TableCell>
              </TableRow>
            ) : (
              users?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.isSuperAdmin && (
                          <Badge variant="secondary">
                            {t("admin.users.superAdmin")}
                          </Badge>
                        )}
                        {user.isEnterprise && (
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            <Building2 className="h-3 w-3 mr-1" />
                            Enterprise
                          </Badge>
                        )}
                        {user.setupPhases && user.setupPhases.total > 0 && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Wrench className="h-3 w-3 mr-1" />
                            {user.setupPhases.paid}/{user.setupPhases.total} phases
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.planType === "pro" ? "default" : "outline"}>
                      {user.plan?.displayName || user.planType}
                    </Badge>
                  </TableCell>
                  <TableCell>{user.credits}</TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? "secondary" : "destructive"}>
                      {user.isActive ? t("common.active") : t("admin.users.suspended")}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatDate(user.createdAt, "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          className="h-8 w-8 p-0"
                          data-testid={`button-user-actions-${user.id}`}
                        >
                          <span className="sr-only">{t("admin.users.openMenu")}</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                        <DropdownMenuItem 
                          onClick={() => handleEdit(user)}
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          {t("admin.users.editDetails")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleOpenPromoCredits(user)}
                          data-testid={`button-add-promo-${user.id}`}
                        >
                          <Gift className="mr-2 h-4 w-4" />
                          {t("admin.users.addPromoCredits")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setSelectedUser(user); resendCredentials.mutate(user.id); }}
                          disabled={resendCredentials.isPending}
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Resend Credentials
                        </DropdownMenuItem>
                        {user.isEnterprise && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-muted-foreground">Enterprise</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleOpenPhasesDialog(user)}>
                              <Wrench className="mr-2 h-4 w-4" />
                              Manage Phases
                            </DropdownMenuItem>
                            {user.enterpriseOnboarding?.recurringMonthlyAmount && parseFloat(user.enterpriseOnboarding.recurringMonthlyAmount) > 0 && (
                              <DropdownMenuItem
                                onClick={() => { setSelectedUser(user); sendRecurringPaymentLink.mutate(user.id); }}
                                disabled={sendRecurringPaymentLink.isPending}
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Send Subscription Link
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => toggleActive(user)}
                        >
                          {user.isActive ? (
                            <>
                              <UserX className="mr-2 h-4 w-4" />
                              {t("admin.users.suspendUser")}
                            </>
                          ) : (
                            <>
                              <UserCheck className="mr-2 h-4 w-4" />
                              {t("admin.users.activateUser")}
                            </>
                          )}
                        </DropdownMenuItem>
                        {!user.isSuperAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleOpenDeleteConfirm(user)}
                              className="text-destructive focus:text-destructive"
                              data-testid={`button-delete-user-${user.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete User
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        pageSize={pagination.pageSize}
        totalItems={pagination.totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.users.editUser")}</DialogTitle>
            <DialogDescription>
              {t("admin.users.updateDetails")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("admin.users.columns.credits")}</Label>
              <Input
                type="number"
                value={editForm.credits}
                onChange={(e) => setEditForm({ ...editForm, credits: parseInt(e.target.value, 10) || 0 })}
                data-testid="input-user-credits"
              />
            </div>
            <div>
              <Label>{t("admin.users.columns.role")}</Label>
              <Select 
                value={editForm.role} 
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger data-testid="select-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="user">{t("admin.users.roles.user")}</SelectItem>
                  <SelectItem value="manager">{t("admin.users.roles.manager")}</SelectItem>
                  <SelectItem value="admin">{t("admin.users.roles.admin")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("admin.users.columns.plan")}</Label>
              <Select 
                value={editForm.planType} 
                onValueChange={(value) => setEditForm({ ...editForm, planType: value })}
              >
                <SelectTrigger data-testid="select-user-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">{t("admin.users.plans.free")}</SelectItem>
                  <SelectItem value="pro">{t("admin.users.plans.pro")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveEdit} data-testid="button-save-user">
              {t("admin.users.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promoCreditsDialog} onOpenChange={setPromoCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.users.promoCreditsTitle")}</DialogTitle>
            <DialogDescription>
              {t("admin.users.promoCreditsDescription", { name: selectedUser?.name || selectedUser?.email })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("admin.users.creditAmount")}</Label>
              <Input
                type="number"
                min="1"
                value={promoCreditsAmount}
                onChange={(e) => setPromoCreditsAmount(parseInt(e.target.value, 10) || 0)}
                placeholder={t("admin.users.enterCreditAmount")}
                data-testid="input-promo-credits"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("admin.users.creditExplanation")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoCreditsDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button 
              onClick={handleAddPromoCredits} 
              disabled={promoCreditsAmount <= 0 || addPromoCredits.isPending}
              data-testid="button-confirm-promo-credits"
            >
              {addPromoCredits.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Gift className="h-4 w-4 mr-2" />
              )}
              {t("admin.users.addCredits")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete User Permanently
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
              </p>
              <p className="text-destructive font-medium">
                This action cannot be undone. All associated data will be permanently removed:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Agents and voice configurations</li>
                <li>Campaigns and contacts</li>
                <li>Call history and recordings</li>
                <li>Flows and automations</li>
                <li>Knowledge base documents</li>
                <li>Subscriptions and credit history</li>
                <li>All other user data</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteUser.isPending}
              data-testid="button-confirm-delete-user"
            >
              {deleteUser.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialog} onOpenChange={setCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Credentials</DialogTitle>
            <DialogDescription>
              {createdCredentials?.emailSent
                ? "Credentials were emailed to the user. You can also copy them below."
                : "Email delivery failed or is disabled. Please share these credentials with the user manually."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="font-mono">{createdCredentials?.email}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="text-muted-foreground">Password</span>
              <div className="flex items-center gap-2">
                <span className="font-mono">{createdCredentials?.password}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    navigator.clipboard.writeText(createdCredentials?.password || '');
                    toast({ title: "Password copied!" });
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredentialsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Phases Dialog */}
      <Dialog open={phasesDialog} onOpenChange={setPhasesDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Manage Setup Phases
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.name} ({selectedUser?.email})
              {selectedUser?.enterpriseOnboarding && (
                <span className="block mt-1">
                  Organization: {selectedUser.enterpriseOnboarding.organizationName}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Enterprise Summary */}
          {selectedUser?.enterpriseOnboarding && (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Plan Amount: </span>
                  <span className="font-medium">
                    {selectedUser.enterpriseOnboarding.currency === 'INR' ? '₹' : '$'}
                    {parseFloat(selectedUser.enterpriseOnboarding.planAmount).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Monthly Recurring: </span>
                  <span className="font-medium">
                    {selectedUser.enterpriseOnboarding.recurringMonthlyAmount && parseFloat(selectedUser.enterpriseOnboarding.recurringMonthlyAmount) > 0
                      ? `${selectedUser.enterpriseOnboarding.currency === 'INR' ? '₹' : '$'}${parseFloat(selectedUser.enterpriseOnboarding.recurringMonthlyAmount).toLocaleString()}/mo`
                      : 'None'
                    }
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Per Minute: </span>
                  <span className="font-medium">
                    {selectedUser.enterpriseOnboarding.currency === 'INR' ? '₹' : '$'}
                    {selectedUser.enterpriseOnboarding.perMinuteRate}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Included Minutes: </span>
                  <span className="font-medium">{selectedUser.enterpriseOnboarding.includedMinutes?.toLocaleString()}</span>
                </div>
              </div>
              {selectedUser.enterpriseOnboarding.recurringMonthlyAmount && parseFloat(selectedUser.enterpriseOnboarding.recurringMonthlyAmount) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => sendRecurringPaymentLink.mutate(selectedUser.id)}
                  disabled={sendRecurringPaymentLink.isPending}
                >
                  {sendRecurringPaymentLink.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Send Subscription Payment Link
                </Button>
              )}
            </div>
          )}

          {/* Phases List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Setup Phases</h4>
              <Button variant="outline" size="sm" onClick={() => setAddPhaseDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Phase
              </Button>
            </div>

            {phasesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : userPhases.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No setup phases defined</p>
                <Button variant="ghost" onClick={() => setAddPhaseDialog(true)}>Add a phase</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {userPhases.map((phase) => {
                  const currencySymbol = selectedUser?.enterpriseOnboarding?.currency === 'INR' ? '₹' : '$';
                  return (
                    <div key={phase.id} className="rounded-lg border p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                            {phase.phaseNumber}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {phase.phaseName}
                              {phase.isRequired && <Badge variant="outline" className="text-xs">Required</Badge>}
                            </div>
                            <div className="text-sm text-muted-foreground">{phase.description}</div>
                            <div className="text-sm font-medium mt-1">
                              {currencySymbol}{parseFloat(phase.amount).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {phase.paymentStatus === 'paid' && (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                              <Check className="h-3 w-3 mr-1" />
                              Paid
                            </Badge>
                          )}
                          {phase.paymentStatus === 'pending' && (
                            <>
                              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => sendPhasePaymentLink.mutate({ userId: selectedUser!.id, phaseId: phase.id })}
                                disabled={sendPhasePaymentLink.isPending}
                              >
                                {sendPhasePaymentLink.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                                Send Link
                              </Button>
                              {phase.stripePaymentLinkUrl && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(phase.stripePaymentLinkUrl!, '_blank')}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              )}
                            </>
                          )}
                          {phase.paymentStatus === 'waived' && (
                            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              Waived
                            </Badge>
                          )}
                          {phase.paymentStatus === 'cancelled' && (
                            <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                              <XCircle className="h-3 w-3 mr-1" />
                              Cancelled
                            </Badge>
                          )}
                        </div>
                      </div>
                      {phase.paymentStatus === 'pending' && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updatePhaseMutation.mutate({
                              userId: selectedUser!.id,
                              phaseId: phase.id,
                              updates: { paymentStatus: 'waived' }
                            })}
                          >
                            Mark as Waived
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => deletePhaseMutation.mutate({ userId: selectedUser!.id, phaseId: phase.id })}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setPhasesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Phase Dialog */}
      <Dialog open={addPhaseDialog} onOpenChange={setAddPhaseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Setup Phase</DialogTitle>
            <DialogDescription>
              Add a new setup/development phase for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Phase Name</Label>
              <Input
                placeholder="e.g., Initial Setup, Development, Training"
                value={newPhase.phaseName}
                onChange={(e) => setNewPhase({ ...newPhase, phaseName: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Description of deliverables"
                value={newPhase.description}
                onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Amount ({selectedUser?.enterpriseOnboarding?.currency === 'INR' ? '₹' : '$'})</Label>
              <Input
                type="number"
                min="0"
                value={newPhase.amount}
                onChange={(e) => setNewPhase({ ...newPhase, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isRequired"
                checked={newPhase.isRequired}
                onChange={(e) => setNewPhase({ ...newPhase, isRequired: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="isRequired" className="text-sm">
                Required before subscription starts
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPhaseDialog(false)}>Cancel</Button>
            <Button
              onClick={() => selectedUser && addPhaseMutation.mutate({ userId: selectedUser.id, phase: newPhase })}
              disabled={!newPhase.phaseName || addPhaseMutation.isPending}
            >
              {addPhaseMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Phase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
